import {IoCloudUploadOutline} from "react-icons/io5";
import {TbFileTypePdf} from "react-icons/tb";
import {BsFiletypeDocx} from "react-icons/bs";
import {RiSlideshowLine} from "react-icons/ri";
import React, {useState, useRef, DragEvent, useCallback} from "react";
import {useTranslation} from "react-i18next";

const ALLOWED_FILE_TYPES = [
    "audio/mpeg", // MP3 files
    "audio/wav", // WAV files
    "audio/ogg", // OGG files
    "audio/aac", // AAC files
    "audio/m4a", // M4A files
    "audio/flac", // FLAC files
    "audio/webm", // WebM audio
    "audio/x-m4a", // Alternative M4A MIME type
];

interface DragAndDropProps {
    onFileUpload: (files: File[]) => void;
    multiple?: boolean;
}

export default function DragAndDrop({
    onFileUpload,
    multiple = false,
}: DragAndDropProps) {
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const {t} = useTranslation("global");
    // Handle file upload from input
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const validFiles = Array.from(event.target.files || []).filter((file) =>
            ALLOWED_FILE_TYPES.includes(file.type)
        );

        if (validFiles.length > 0) {
            onFileUpload(multiple ? validFiles : [validFiles[0]]);
        }
    };

    // Drag and drop handlers
    const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragStart = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragEnd = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files);
        const validFiles = files.filter((file) =>
            ALLOWED_FILE_TYPES.includes(file.type)
        );
        if (validFiles.length > 0) {
            onFileUpload(multiple ? validFiles : [validFiles[0]]);
        }
    };

    return (
        <div
            className={`flex flex-col justify-center items-center border-dashed border-[2px] rounded-xl w-full h-[120px] transition-colors duration-300 ${
                isDragging
                    ? "bg-primary/20 border-primary shadow-lg border-[2px] "
                    : "bg-[#F3F4F6] bg-opacity-50 border-[#D9D9D9] "
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            {!isDragging ? (
                <>
                    {/* Upload instructions */}
                    <div>
                        <div className="flex flex-col text-[14px] sm:flex-row justify-center items-center  gap-2 mt-2">
                            <div className="text-[14px] font-light text-normal text-secondary text-center">
                                <span className="sm:block hidden">
                                    {t("upload.dragAndDrop_1_short")}
                                </span>
                                <span className="block sm:hidden">
                                    {t("upload.dragAndDrop_1_long")}
                                </span>
                            </div>
                            <div
                                className="flex flex-row justify-center items-center text-primary underline cursor-pointer"
                                onClick={() => {
                                    fileInputRef.current?.click();
                                }}
                            >
                                {t("upload.dragAndDrop_browse")}
                            </div>
                            <div className="text-[14px] font-light text-normal text-secondary block">
                                {t("upload.dragAndDrop_2")}
                            </div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                onChange={handleFileUpload}
                                accept="audio/*"
                                multiple={multiple}
                            />
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div
                        className={`flex flex-col justify-center items-center gap-2 mt-2 transition-all duration-300`}
                    >
                        <IoCloudUploadOutline
                            className="w-[40px] h-[40px] animate-pulse text-primary"
                            style={{
                                animation: "scaleLoop 2s ease-in-out infinite",
                            }}
                        />
                        <p className="text-secondary font-light text-[14px]">
                            {t("upload.drop")}
                        </p>
                    </div>
                    <style>
                        {`
                        @keyframes scaleLoop {
                            0%,
                            100% {
                                transform: scale(1);
                            }
                            50% {
                                transform: scale(1.4);
                            }
                        }
                        `}
                    </style>
                </>
            )}
        </div>
    );
}
